import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  AlertTriangle,
  ArrowUpRight,
  Link2,
  Loader2,
  Pause,
} from 'lucide-react';

import { toaster } from '@/shared/libs';
import { Button, Dialog, DialogFooter } from '@/shared/ui';
import { DEAL_STATUS_BADGE_CLASS, DEAL_STATUS_I18N_KEY } from '@entities/Deal';
import {
  useProviderAvailabilityImpact,
  useSetProviderStatusMutation,
} from '@entities/Provider';
import type { DealStatus, Provider } from '@psp-aggregator/sdk';

export interface PauseProviderModalProps {
  onClose: () => void;
  onReviewDeals?: () => void;
  provider: Provider;
}

export const PauseProviderModal: FC<PauseProviderModalProps> = ({
  onClose,
  onReviewDeals,
  provider,
}) => {
  const { t } = useTranslation();
  const impact = useProviderAvailabilityImpact(provider.id, true);
  const setStatus = useSetProviderStatusMutation();
  const dealsByStatus = useMemo(() => {
    const counts = new Map<DealStatus, number>();

    for (const deal of impact.data?.active_deals ?? []) {
      counts.set(deal.status, (counts.get(deal.status) ?? 0) + 1);
    }

    return [...counts.entries()];
  }, [impact.data?.active_deals]);

  const handleConfirm = async (): Promise<void> => {
    try {
      await setStatus.mutateAsync({
        providerId: provider.id,
        status: 'paused',
      });
      toaster.success({ title: t('toast.providerPaused') });
      onClose();
    } catch (error) {
      toaster.error({
        title: isAxiosError(error)
          ? (error.response?.data?.message ??
            t('errors.providers.statusFailed'))
          : t('errors.unknown'),
      });
    }
  };

  return (
    <Dialog
      title={t('pages.admin.providers.pause.title', {
        name: provider.real_name,
      })}
      onClose={onClose}>
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500/10">
          <AlertTriangle className="h-5 w-5 text-amber-500" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {t('pages.admin.providers.pause.title', {
              name: provider.real_name,
            })}
          </p>
          {!!provider.alias && (
            <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
              {provider.alias}
            </p>
          )}
        </div>
      </div>
      {impact.isLoading ? (
        <Loader2 className="mb-4 h-4 w-4 animate-spin text-muted-foreground" />
      ) : (
        <>
          <p className="mb-4 text-sm">
            {t('pages.admin.providers.pause.affectedOffers', {
              count: impact.data?.affected_offers_count ?? 0,
            })}
          </p>
          {(impact.data?.affected_deals_count ?? 0) > 0 && (
            <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-3.5">
              <p className="mb-2.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wider text-amber-600 uppercase dark:text-amber-400">
                <Link2 className="h-3.5 w-3.5" />
                {t('pages.admin.providers.pause.affectedDeals', {
                  count: impact.data?.affected_deals_count ?? 0,
                })}
              </p>
              <div className="mb-3 flex flex-wrap gap-1.5">
                {dealsByStatus.map(([status, count]) => (
                  <span
                    key={status}
                    className={`inline-flex rounded-full px-2 py-1 text-[11px] font-medium ${DEAL_STATUS_BADGE_CLASS[status]}`}>
                    {t(DEAL_STATUS_I18N_KEY[status])}: {count}
                  </span>
                ))}
              </div>
              {!!onReviewDeals && (
                <Button
                  className="w-full"
                  type="button"
                  variant="outline"
                  onClick={onReviewDeals}>
                  <ArrowUpRight className="h-3.5 w-3.5" />
                  {t('pages.admin.providers.pause.reviewDeals')}
                </Button>
              )}
            </div>
          )}
        </>
      )}

      <DialogFooter
        endSlot={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('buttons.cancel')}
            </Button>
            <Button
              className="bg-amber-500 text-white hover:bg-amber-600"
              disabled={setStatus.isPending}
              type="button"
              onClick={() => {
                void handleConfirm();
              }}>
              {setStatus.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Pause className="h-4 w-4" />
                  {t('pages.admin.providers.pause.confirm')}
                </>
              )}
            </Button>
          </>
        }
      />
    </Dialog>
  );
};

PauseProviderModal.displayName = 'PauseProviderModal';
